import React, { useState, useMemo } from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import Image from 'next/image';
import { LuCircleCheck, LuCircleX } from 'react-icons/lu';
import {
  PhonicsLessonGroup,
  PhonicsLesson,
  PhonicsClassStatus,
  PhonicsLessonProgress,
  PhonicsStudentActivityResponse,
} from '@/api/types';
import {
  Button,
  Table,
  TableColumns,
  Image as PhonicsImage,
} from '@/components';
import { ManageBreadcrumb } from './ManageBreadcrumb';
import { ManageLessonLayout } from './ManageLessonLayout';
import { ManagePanel } from './ManagePanel';

interface ManageLessonResultsProps {
  lesson: PhonicsLesson;
  phonicsClass: PhonicsClassStatus;
  onViewPlan: (lesson: PhonicsLesson) => void;
  onAddNotes: (lesson: PhonicsLesson) => void;
  onGetYearGroup: (yearGroupId: string) => PhonicsLessonGroup | undefined;
}

interface LessonResultActivityStatus {
  isComplete: boolean;
  score?: number;
  responses?: Array<PhonicsStudentActivityResponse>;
}

type LessonResultActivityStatuses = Record<string, LessonResultActivityStatus>;

type LessonResultRow = {
  id: string;
  name: string;
  activities: LessonResultActivityStatuses;
};

export const ManageLessonResults = ({
  lesson,
  phonicsClass,
  onViewPlan,
  onAddNotes,
  onGetYearGroup,
}: ManageLessonResultsProps) => {
  const [expandedRows, setExpandedRows] = useState<Array<LessonResultRow>>([]);

  const [rowsState, setRowsState] = useState<Record<string, string>>({});

  const avatars = [
    'apple',
    'camel',
    'cat',
    'chicken',
    'cow',
    'dog',
    'elephant',
    'hamster',
    'monkey',
    'octopus',
    'rabbit',
    'raccoon',
  ];

  const rows: Array<LessonResultRow> = useMemo(() => {
    if (!lesson || !phonicsClass.studentStatuses) {
      return [];
    }

    return phonicsClass.studentStatuses.map((studentStatus) => ({
      id: studentStatus.studentId,
      name: studentStatus.name,
      activities: lesson.activities
        .filter((activity) => activity.type.mode === 'LEARN')
        .reduce((acc, activity) => {
          const activityStatus = studentStatus.lessonStatuses
            .find((lessonStatus) => lessonStatus.lessonId === lesson.id)
            ?.activityStatuses.find(
              (activityStatus) => activityStatus.activityId === activity.id,
            );

          const isComplete =
            activityStatus?.progress === PhonicsLessonProgress.COMPLETED;

          const totalScore = activityStatus?.responses
            ? activityStatus.responses.reduce((score, response) => {
                if (response.score) {
                  score += response.score;
                }

                return score;
              }, 0)
            : 0;

          return {
            ...acc,
            [activity.id]: {
              isComplete,
              score: activityStatus?.responses?.length
                ? totalScore / activityStatus.responses.length
                : undefined,
              responses: activityStatus?.responses,
            },
          };
        }, {}),
    }));
  }, [lesson, phonicsClass]);

  return (
    <div className="flex flex-col gap-6">
      <ManageBreadcrumb
        section="lessons"
        weekGroup={lesson.group || undefined}
        lesson={lesson}
        suffix={<strong className="font-bold">Results</strong>}
      />

      <ManageLessonLayout
        lesson={lesson}
        phonicsClass={phonicsClass}
        onGetYearGroup={onGetYearGroup}
      >
        <div className="flex gap-3">
          <Button
            style="secondary"
            onClick={() => onAddNotes(lesson)}
            tooltipText="Add notes to this lesson."
          >
            Add Lesson Notes
          </Button>

          <Button
            onClick={() => onViewPlan(lesson)}
            tooltipText="View the lesson plan for this lesson."
          >
            View Lesson Plan
          </Button>
        </div>

        <ManagePanel bgColorScheme="white">
          <Table
            className="w-full"
            columns={{
              name: {
                label: 'Name',
                className: 'flex gap-3 items-center',
                render: (fieldName, row, i) => (
                  <>
                    <PhonicsImage
                      src={`/images/avatars/${avatars[i % avatars.length]}.png`}
                      width={64}
                      height={64}
                      alt={row.name}
                      className="w-8 h-auto"
                    />
                    <button
                      className="text-lg text-pt-primary underline hover:no-underline"
                      onClick={() =>
                        setExpandedRows((prev) =>
                          prev.includes(row)
                            ? prev.filter((r) => r !== row)
                            : [...prev, row],
                        )
                      }
                    >
                      {row.name}
                    </button>
                    <Link
                      className="text-sm text-pt-primary underline hover:no-underline"
                      href={`/manage/students/${row.id}?back=/manage/lessons/${lesson.id}/results`}
                    >
                      (View detail)
                    </Link>
                  </>
                ),
              },
              ...lesson.activities
                .filter((activity) => activity.type.mode === 'LEARN')
                .reduce(
                  (acc, activity) => ({
                    ...acc,
                    [activity.id]: {
                      label: activity.type.name,
                      align: 'center',
                      headingClassName: 'capitalize',
                      render: (fieldName, row) => (
                        <button
                          className={clsx(
                            'flex w-full h-full justify-center items-center',
                            expandedRows.includes(row) &&
                              rowsState?.[row.id] &&
                              rowsState[row.id] === fieldName &&
                              "relative after:content-[''] after:absolute after:-bottom-[22px] after:left-1/2 after:transform after:-translate-x-1/2 after:border-l-[10px] after:border-r-[10px] after:border-b-[12px] after:border-l-transparent after:border-r-transparent after:border-b-pt-primary",
                          )}
                          onClick={() => {
                            setRowsState((prev) => ({
                              ...prev,
                              [row.id]: fieldName,
                            }));

                            setExpandedRows((prev) => [...prev, row]);
                          }}
                        >
                          {row.activities[fieldName].isComplete ? (
                            <div className="flex justify-center space-x-2">
                              <LuCircleCheck className="w-6 h-6 text-green-400 flex-none" />
                              <span className="sr-only">Completed</span>
                              {!!row.activities[fieldName].score && (
                                <span className="font-bold">
                                  {Math.round(row.activities[fieldName].score)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="flex justify-center space-x-2">
                              <LuCircleX className="w-6 h-6 text-red-400" />
                            </div>
                          )}
                        </button>
                      ),
                    },
                  }),
                  {} as TableColumns<any>, // eslint-disable-line @typescript-eslint/no-explicit-any
                ),
            }}
            rows={rows}
            expandedRows={expandedRows}
            getExpandedContent={(studentRow) => {
              if (!rowsState[studentRow.id]) {
                setRowsState((prev) => ({
                  ...prev,
                  [studentRow.id]: Object.keys(studentRow.activities)[0],
                }));
              }

              return (
                <div className="bg-pt-primary pt-3">
                  <h2 className="text-xl font-bold mx-6 text-white">
                    {
                      lesson.activities.find(
                        (activity) =>
                          activity.id === rowsState?.[studentRow.id],
                      )?.type.name
                    }
                  </h2>

                  <Table
                    columns={{
                      symbol: {
                        label: 'Symbol',
                        render: (fieldName, row) => row.text as string,
                      },
                      score: {
                        label: 'Score',
                        render: (fieldName, row) => row.score as number,
                      },
                      image: {
                        label: 'Image',
                        render: (fieldName, row) =>
                          row.base64Image && (
                            <Image
                              src={row.base64Image as string}
                              width="100"
                              height="100"
                              alt={row.text as string}
                            />
                          ),
                      },
                      unknown: {
                        label: '',
                        render: () => '',
                      },
                    }}
                    rows={
                      studentRow.activities[rowsState?.[studentRow.id]]
                        ?.responses || []
                    }
                    striped
                    inverted
                  />
                </div>
              );
            }}
            striped
          />
        </ManagePanel>
      </ManageLessonLayout>
    </div>
  );
};
